export type Pain = {
  /** The setup, set large and bold. */
  lead: string
  /** The consequence after the natural pause, set smaller and muted. */
  rest?: string
}

export const pains: Pain[] = [
  {
    lead: 'You take the screen away…',
    rest: 'and the crying, yelling or tantrum starts.',
  },
  {
    lead: 'Sometimes you give it back just to get a little peace…',
    rest: 'and then the guilt hits.',
  },
  {
    lead: 'It hurts watching them spend hours staring at cartoons like a zombie,',
    rest: 'not knowing what to do without a screen.',
  },
  {
    lead: 'You worry constant stimulation is slowly replacing their creativity,',
    rest: 'focus and desire to make things themselves.',
  },
  {
    lead: 'In the age of AI, it scares you that they may learn to consume everything…',
    rest: 'but not create, imagine, solve problems or think for themselves.',
  },
]

export type Category = {
  emoji: string
  title: string
  description: string
  image: string
  alt: string
}

export const categories: Category[] = [
  {
    emoji: '🦁',
    title: 'Animals & Wildlife',
    description: 'Lions, elephants, dogs, cats, jungle animals, ocean creatures and more.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e51476f1-63de-4104-9d8e-cd9d1859b4ee.jpg',
    alt: 'Paper craft lion, elephant and fox made from printed cardstock',
  },
  {
    emoji: '🦖',
    title: 'Dinosaurs & Fantasy',
    description: 'Dinosaurs, dragons, mythical creatures and imaginative builds.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/88f43370-c83a-4d60-81c4-83f816cc1232.jpg',
    alt: 'Paper craft dinosaurs and a dragon made from printed cardstock',
  },
  {
    emoji: '🚗',
    title: 'Cars, Vehicles & Machines',
    description: 'Cars, trucks, planes, boats, rockets and machines to assemble.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/56469926-968b-4960-9c31-4b655212f9bc.jpg',
    alt: 'Paper craft race car, airplane and rocket made from printed cardstock',
  },
  {
    emoji: '🤖',
    title: 'Robots & Characters',
    description: 'Creative paper characters, robots and playful figures.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/489bc92a-c6f4-42ba-b339-7e1db7bbe556.jpg',
    alt: 'Paper craft robots and characters made from printed cardstock',
  },
  {
    emoji: '🏰',
    title: 'Buildings & Worlds',
    description: 'Houses, castles, structures and miniature worlds.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/f1ae3f70-06c0-4aa0-908b-728072b9ee24.jpg',
    alt: 'Paper craft castle and houses forming a miniature world',
  },
  {
    emoji: '🎭',
    title: 'Masks & Creative Crafts',
    description: 'Printable masks, costumes and paper creations for imaginative play.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/571bdf39-6f37-4815-be37-438fead8d578.jpg',
    alt: 'Printable paper craft fox and lion masks made from cardstock',
  },
]

export type Step = {
  number: string
  emoji: string
  title: string
  description: string
  image: string
  alt: string
}

export const steps: Step[] = [
  {
    number: '01',
    emoji: '🖨️',
    title: 'PRINT',
    description: 'Choose a project and print the template at home.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a218fde1-9d9c-4823-adac-e5200a2f0974.jpg',
    alt: 'Home printer printing a paper craft template sheet with cut lines',
  },
  {
    number: '02',
    emoji: '✂️',
    title: 'CUT & FOLD',
    description: 'Follow the simple lines, cut the pieces and fold where indicated.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/97ca8faf-2770-4764-b9f0-5b67f6d64318.jpg',
    alt: "A child's hands cutting a printed paper craft template with scissors",
  },
  {
    number: '03',
    emoji: '🦖',
    title: 'BUILD & PLAY',
    description: 'Watch a flat sheet of paper become something they created themselves.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8915029b-5a11-4d39-9e50-65505426d2b0.jpg',
    alt: 'A child proudly holding a finished paper craft dinosaur',
  },
]

export type Benefit = {
  /** The promise, set large and bold. */
  lead: string
  /** The payoff after the natural pause, set smaller and muted. */
  rest?: string
}

export const benefits: Benefit[] = [
  {
    lead: 'You’ll watch your child stay focused building something with their own hands…',
    rest: 'while the phone sits forgotten nearby.',
  },
  {
    lead: 'You’ll have more quiet time to work, cook, finish what you need to do — or simply breathe —',
    rest: 'while they’re happily occupied creating.',
  },
  {
    lead: 'You’ll enjoy moments of peace',
    rest: 'without feeling like the only way to get them is by putting another screen in front of your child.',
  },
  {
    lead: 'You’ll watch them imagine, try, make mistakes, solve problems',
    rest: 'and proudly finish something all by themselves.',
  },
  {
    lead: 'You’ll go to bed feeling like you did something good for them today…',
    rest: 'instead of wondering whether another afternoon of screens makes you a bad parent.',
  },
  {
    lead: 'You’ll watch them grow accustomed to creating, thinking and solving problems —',
    rest: 'the skills they’ll need most in the world ahead.',
  },
]

export type Bonus = {
  title: string
  description: string
  image: string
  alt: string
}

export const bonuses: Bonus[] = [
  {
    title: 'Rainy Day Emergency Pack',
    description: 'A curated set of quick paper projects perfect for long afternoons and rainy days.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/48a0cd97-c891-4cdb-93af-8bbd3ebe1003.jpg',
    alt: 'Printed paper craft template sheets beside a rainy window',
  },
  {
    title: 'Easy Builds for Beginners',
    description: 'A collection of simpler projects for younger children or their first builds.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/065b59f1-b203-4676-954f-d91ec19f7b32.jpg',
    alt: 'Simple paper craft templates and easy finished paper figures',
  },
  {
    title: 'Screen-Free Weekend Challenge',
    description: 'A simple printable challenge that turns building into a fun family weekend activity.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/7f625aa3-ea61-4e36-982a-f6b0198cf2fb.jpg',
    alt: 'Printable challenge sheet with checkboxes beside finished paper models',
  },
  {
    title: 'Creative Play Ideas',
    description: 'Ways to turn finished paper models into games, stories and imaginative play.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/5ae82bad-20ea-426d-baa5-6ea3e833e337.jpg',
    alt: 'Finished paper craft animals arranged as a play scene',
  },
  {
    title: 'Printable Project Tracker',
    description: 'Kids can mark every model they complete and choose what they want to build next.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/19a4a464-8a6b-4580-9a3c-d2fae194aeb9.jpg',
    alt: 'Printable project tracker sheet with empty checkboxes on a wall',
  },
  {
    title: 'Favorites Collection',
    description: 'A curated collection of the most exciting models to help parents choose quickly.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/67f3a7f8-851a-452a-883e-703ce77cdab7.jpg',
    alt: 'Flat template, cut pieces and a finished paper elephant side by side',
  },
]

export type Faq = {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  {
    question: 'Is this a physical kit?',
    answer:
      'No. The 3D Paper Craft Mega Kit is 100% digital. You’ll receive access to printable files that you can download and print at home.',
  },
  {
    question: 'What do I need to get started?',
    answer: 'A printer, paper or cardstock, scissors and glue are enough for most projects.',
  },
  {
    question: 'Do I need a special printer?',
    answer: 'No. The templates are designed to work with a normal home printer.',
  },
  {
    question: 'What age is it for?',
    answer:
      'The collection includes projects with different levels of difficulty. Younger children may need help cutting or assembling more detailed models.',
  },
  {
    question: 'Can I print the projects more than once?',
    answer: 'Yes. Once downloaded, you can print your favorite projects again for personal use.',
  },
  {
    question: 'Will I get access immediately?',
    answer: 'Yes. After completing your purchase, you’ll receive instructions for accessing the digital files.',
  },
  {
    question: 'Is there a subscription?',
    answer: 'No. It’s a one-time purchase.',
  },
  {
    question: 'What if my child doesn’t like it?',
    answer: 'You’re protected by our 7-day guarantee. See the guarantee terms above for details.',
  },
]

export const galleryImages = [
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/50e839ea-0821-4c81-8475-014dbd40edf1.jpg',
    alt: 'Laptop and tablet showing the printable file library beside printed templates and colorful finished paper toys',
    label: 'Mega Kit',
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/a795794c-cb16-4034-9822-8e20d22ce0b4.jpg',
    alt: 'Many finished paper craft animals arranged in rows',
    label: 'Animals',
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/b3e25873-af2d-4fe0-bf59-e900a54e37cb.jpg',
    alt: 'Many finished paper craft vehicles and robots arranged in rows',
    label: 'Vehicles',
  },
  {
    src: 'https://cdn.magicpatterns.com/patterns/generated-images/67f3a7f8-851a-452a-883e-703ce77cdab7.jpg',
    alt: 'Flat printed template, cut and folded pieces, and a finished paper elephant',
    label: 'Print → Build',
  },
]

export const heroImage = {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/8e955cca-81f7-44e2-bafa-1000bf02328f.jpg',
  alt: 'Dozens of colorful finished 3D paper toys — dinosaurs, robots, a castle, a race car and animals — beside printed templates, scissors and a home printer',
}

export const finalImage = {
  src: 'https://cdn.magicpatterns.com/patterns/generated-images/c010bc00-2d2d-43ce-88a6-91e5ff2412c3.jpg',
  alt: 'A large colorful collection of finished 3D paper toys arranged in rows: dinosaurs, robots, vehicles, a castle and masks',
}
